/**
 * The field rules for names, business names, email addresses, web addresses
 * and free text (shared-react-ui#81, shared-audit#17) — the client twin of
 * shared-pweb-common `pweb_common.fields`, check for check and code for code.
 * The shared `cases.json` runs against both; change a rule in both, and in
 * the cases, together.
 *
 * Pure TypeScript, no React and no dependency, so the chat widget can bundle
 * it (`@puglieseweb/shared-react-ui/fields`).
 *
 * Where the copies this replaces disagreed, the rule here is the one
 * shared-audit#17 states; where #17 is silent, shared-auth's (the server's)
 * for what is accepted — a form must never accept what a server refuses — and
 * shared-react-auth's for how finely a failure is named.
 *
 * Two things are deliberately NOT JavaScript's defaults, so the two languages
 * measure alike: lengths count Unicode code points (`Array.from`, Python's
 * `len`), and white space is one explicit set (`WS`: JavaScript's `\s` adds
 * U+FEFF, Python's adds U+001C-U+001F and U+0085).
 */
import type { FieldCode, FieldParams, FieldResult, RequiredOption } from './types';

// ── characters (pweb_common.fields › _WS, _is_control) ─────────────────────

const WS = ' \\t\\n\\v\\f\\r\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000';
const WS_RUN = new RegExp(`[${WS}]+`, 'g');
const WS_EDGES = new RegExp(`^[${WS}]+|[${WS}]+$`, 'g');
const HAS_WS = new RegExp(`[${WS}]`);

/**
 * A character with no place in a name or a line of text: C0/C1 controls,
 * surrogates, private use, unassigned code points, line and paragraph
 * separators, and the bidi overrides and isolates that reorder what is
 * DISPLAYED. The two joiners (U+200C, U+200D) are format characters and pass:
 * several scripts are written with them.
 */
export const CONTROL = /[\p{Cc}\p{Cs}\p{Co}\p{Cn}\u2028\u2029\u202a-\u202e\u2066-\u2069]/u;
const CONTROL_ALL = new RegExp(CONTROL.source, 'gu');

/** Trim the shared white-space set from both ends. */
export function trimSpaces(value: string): string {
  return value.replace(WS_EDGES, '');
}

/** Trim, and collapse runs of white space to one space. */
export function collapseSpaces(value: string): string {
  return trimSpaces(value.replace(WS_RUN, ' '));
}

/** Length in Unicode code points — what the servers count. */
export function codePointLength(value: string): number {
  return Array.from(value).length;
}

export function ok(value: string): FieldResult {
  return { ok: true, value, problem: null };
}

export function no(code: FieldCode, value = '', params: FieldParams = {}): FieldResult {
  return { ok: false, value, problem: { code, params } };
}

/** `null`/`undefined` are nothing typed; any other non-string is not text. */
function textOf(value: unknown): string | null {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : null;
}

/** Show "n / max" once a value reaches 90 % of its limit (shared-audit#17). */
export function counterFrom(max: number): number {
  return Math.ceil(max * 0.9);
}

// ── person names ───────────────────────────────────────────────────────────

/** Each of first and last name. */
export const PERSON_NAME_MAX = 50;
/** The joined full name (`custom:fullName`): two names of 50 and a space. */
export const FULL_NAME_MAX = 2 * PERSON_NAME_MAX + 1;

// Letters of any script, their combining marks, a space, - ' ’ . and the joiners.
const NAME_CHARACTERS = /^[\p{L}\p{M} '’.\u200c\u200d-]+$/u;
const HAS_LETTER = /\p{L}/u;
// A name never contains a host: "www.", or a word glued to a common TLD.
// Character for character shared-react-auth's WEB_ADDRESS and shared-auth's
// _WEB_ADDRESS_RE: 'Jean.Pierre', 'J.R.R.' and 'St. John' pass.
const WEB_ADDRESS = /(?:^|[\s.])www\.|\p{L}{2,}\.(?:com|net|org|info|biz|io|app|xyz|online|site|top|shop|link|click|ru|cn|uk|it)(?:$|[\s.])/iu;

/**
 * A first or last name (`max` 50), or a whole name (`max` 101). Trimmed with
 * inner white space collapsed. Codes, in this order:
 * `name.invalid_characters` (a control character or line break anywhere,
 * BEFORE collapsing; then anything but letters, marks, spaces, `-'’.`),
 * `name.required`, `name.too_long` (`max`), `name.needs_letter`,
 * `name.web_address`.
 */
export function checkPersonName(value: unknown, options: { max?: number } & RequiredOption = {}): FieldResult {
  const max = options.max ?? PERSON_NAME_MAX;
  const raw = textOf(value);
  if (raw === null) return no('name.invalid_characters');
  if (CONTROL.test(raw)) return no('name.invalid_characters', trimSpaces(raw));
  const clean = collapseSpaces(raw);
  if (!clean) return options.required === false ? ok('') : no('name.required');
  if (codePointLength(clean) > max) return no('name.too_long', clean, { max });
  if (!NAME_CHARACTERS.test(clean)) return no('name.invalid_characters', clean);
  if (!HAS_LETTER.test(clean)) return no('name.needs_letter', clean);
  if (WEB_ADDRESS.test(clean)) return no('name.web_address', clean);
  return ok(clean);
}

/**
 * A STORED full name as it may be shown: the rule's cleaned value (`max`
 * 101), or '' when it fails the rule — "no name", which each caller already
 * has an answer for.
 */
export function displayPersonName(value: unknown): string {
  const result = checkPersonName(value, { max: FULL_NAME_MAX, required: false });
  return result.ok ? result.value : '';
}

// ── business names ─────────────────────────────────────────────────────────

export const BUSINESS_NAME_MIN = 2;
export const BUSINESS_NAME_MAX = 120;

/**
 * An organisation's name: trimmed, inner white space collapsed, 2-120. Codes:
 * `business_name.invalid_characters` (`<`, `>`, a control character or a line
 * break anywhere — before collapsing, so a pasted two-line name is refused,
 * not joined), `business_name.required`, `business_name.too_short` (`min`),
 * `business_name.too_long` (`max`).
 */
export function checkBusinessName(value: unknown, options: RequiredOption = {}): FieldResult {
  const raw = textOf(value);
  if (raw === null) return no('business_name.invalid_characters');
  if (raw.includes('<') || raw.includes('>') || CONTROL.test(raw)) {
    return no('business_name.invalid_characters', trimSpaces(raw));
  }
  const clean = collapseSpaces(raw);
  if (!clean) return options.required === false ? ok('') : no('business_name.required');
  const length = codePointLength(clean);
  if (length < BUSINESS_NAME_MIN) return no('business_name.too_short', clean, { min: BUSINESS_NAME_MIN });
  if (length > BUSINESS_NAME_MAX) return no('business_name.too_long', clean, { max: BUSINESS_NAME_MAX });
  return ok(clean);
}

// ── email ──────────────────────────────────────────────────────────────────

export const EMAIL_MAX = 254;
export const EMAIL_LOCAL_MAX = 64;
// The local part admits the ASCII apostrophe (RFC 5322 atext: o'brien@,
// dell'acqua@; shared-audit#18), never the typographic ’. It also admits
// '%': 'victim%40real.example%2Cthief@evil.example' is ONE mailbox at
// evil.example — never percent-DECODE an address (`mailtoHref` encodes it).
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+'-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const LOCAL_CHARACTERS = /^[a-zA-Z0-9._%+'-]+$/;
const DOMAIN_SHAPE = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * An email address, trimmed and lower-cased (what every server stores). At
 * most 254, one `@`, a local part of at most 64 in `[a-zA-Z0-9._%+'-]`
 * without a leading, trailing or doubled dot, domain labels of 1-63 without
 * an edge hyphen, a TLD of two letters or more. Codes, in this order:
 * `email.required`, `email.too_long` (`max`), `email.spaces`, `email.no_at`,
 * `email.several_at`, `email.invalid_local_part` (`max` 64),
 * `email.invalid_domain`; `email.invalid` for a value that is not text.
 */
export function checkEmail(value: unknown, options: RequiredOption = {}): FieldResult {
  const raw = textOf(value);
  if (raw === null) return no('email.invalid');
  const email = trimSpaces(raw).toLowerCase();
  if (!email) return options.required === false ? ok('') : no('email.required');
  if (codePointLength(email) > EMAIL_MAX) return no('email.too_long', email, { max: EMAIL_MAX });
  if (HAS_WS.test(email)) return no('email.spaces', email);
  const ats = email.split('@').length - 1;
  if (ats === 0) return no('email.no_at', email);
  if (ats > 1) return no('email.several_at', email);
  const [local, domain] = email.split('@');
  if (
    !local
    || codePointLength(local) > EMAIL_LOCAL_MAX
    || !LOCAL_CHARACTERS.test(local)
    || local.startsWith('.')
    || local.endsWith('.')
    || local.includes('..')
  ) {
    return no('email.invalid_local_part', email, { max: EMAIL_LOCAL_MAX });
  }
  if (
    !DOMAIN_SHAPE.test(domain)
    || domain.split('.').some((l) => !l || l.length > 63 || l.startsWith('-') || l.endsWith('-'))
  ) {
    return no('email.invalid_domain', email);
  }
  return EMAIL_PATTERN.test(email) ? ok(email) : no('email.invalid_domain', email);
}

/**
 * A `mailto:` for an address, or null when the address fails `checkEmail`.
 * The local part is `encodeURIComponent`-ed as well, so no stored address —
 * even one that predates the rule — can add a `cc`, a `bcc` or a body to the
 * draft it opens (product-ema-contacts#66), and a '%' reaches the mail client
 * as a literal '%' (`%25`), never decoded into a second recipient.
 */
export function mailtoHref(email: string, query: Readonly<Record<string, string>> = {}): string | null {
  const checked = checkEmail(email);
  if (!checked.ok) return null;
  const address = checked.value;
  const at = address.lastIndexOf('@');
  const encoded = `${encodeURIComponent(address.slice(0, at))}@${address.slice(at + 1)}`;
  const params = Object.entries(query)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  return `mailto:${encoded}${params ? `?${params}` : ''}`;
}

// ── web addresses ──────────────────────────────────────────────────────────

/** An organisation's website (shared-auth MAX_WEBSITE_LENGTH). */
export const WEBSITE_MAX = 200;
/** A link a person chose (shared-content WEB_ADDRESS_MAX). */
export const LINK_MAX = 2048;

const SCHEME = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//;
// shared-auth's host: dot-separated labels without white space, URL
// punctuation, brackets, a backslash or a control character, an optional
// trailing root dot and an optional port.
const HOST_CHAR = `[^${WS}/?#@:.\\[\\]\\\\\\u0000-\\u001f\\u007f]`;
const HOST = new RegExp(`^${HOST_CHAR}+(?:\\.${HOST_CHAR}+)+\\.?(?::[0-9]{1,5})?$`, 'u');
// Names that never resolve on the public internet (shared-auth _PRIVATE_SUFFIXES).
const PRIVATE_SUFFIXES = ['localhost', 'local', 'localdomain', 'internal', 'intranet', 'lan', 'home', 'corp', 'private', 'home.arpa'];

/**
 * False for an IP literal in any spelling a resolver takes for one (a TLD is
 * never all digits: '10.1', '127.0.0.1', '0x7f.1') and for localhost or a
 * private-network suffix.
 */
function isPublicHost(host: string): boolean {
  const name = (host.includes(':') ? host.slice(0, host.lastIndexOf(':')) : host).replace(/\.+$/, '');
  const last = name.slice(name.lastIndexOf('.') + 1);
  if (/^[0-9]+$/.test(last) || last.startsWith('0x')) return false;
  return !PRIVATE_SUFFIXES.some((suffix) => name === suffix || name.endsWith(`.${suffix}`));
}

/**
 * A web address a person typed: a bare domain or an `http(s)://` address of
 * a public host with a dot in it, without a `user@`, at most `max` (200 for
 * a website, 2048 for a link). The value is canonical: a bare domain gains
 * `https://`, scheme and host are lower-cased, trailing slashes dropped.
 * `http://` is kept as typed unless `httpsOnly`, which refuses it
 * (`url.not_https`) when its https twin would be fine.
 *
 * Codes, in this order: `url.required`, `url.too_long` (`max`),
 * `url.invalid` (white space or a control character, another scheme, a
 * `user@`, no host), `url.not_public`, `url.invalid` (not a dotted host),
 * `url.not_https`.
 */
export function checkWebUrl(
  value: unknown,
  options: { httpsOnly?: boolean; max?: number } & RequiredOption = {},
): FieldResult {
  const max = options.max ?? WEBSITE_MAX;
  const text = textOf(value);
  if (text === null) return no('url.invalid');
  const raw = trimSpaces(text);
  if (!raw) return options.required === false ? ok('') : no('url.required');
  if (codePointLength(raw) > max) return no('url.too_long', raw, { max });
  if (HAS_WS.test(raw) || CONTROL.test(raw)) return no('url.invalid', raw);
  const schemeMatch = raw.match(SCHEME);
  let scheme = 'https';
  let rest = raw;
  if (schemeMatch) {
    scheme = schemeMatch[0].slice(0, -3).toLowerCase();
    if (scheme !== 'http' && scheme !== 'https') return no('url.invalid', raw);
    rest = raw.slice(schemeMatch[0].length);
  }
  const typedHost = rest.split(/[/?#]/, 1)[0];
  const path = rest.slice(typedHost.length);
  const host = typedHost.toLowerCase();
  if (!host || host.includes('@')) return no('url.invalid', raw);
  if (!isPublicHost(host)) return no('url.not_public', raw);
  if (!HOST.test(host)) return no('url.invalid', raw);
  if (options.httpsOnly && scheme === 'http') return no('url.not_https', raw);
  return ok(`${scheme}://${host}${path.replace(/\/+$/, '')}`);
}

// ── free text ──────────────────────────────────────────────────────────────

const MULTILINE_ALLOWED = new Set(['\n', '\r', '\t']);

/**
 * A line (`oneLine`, the default) or a paragraph of free text, trimmed and
 * REFUSED over its limit — never cut. Codes, in this order: `text.required`,
 * `text.too_long` (`max`, `count`), `text.too_short` (`min`, `count`),
 * `text.invalid_characters` (a control character or a bidi override; a line
 * break too unless `oneLine` is false).
 */
export function checkText(
  value: unknown,
  options: { max: number; min?: number; oneLine?: boolean } & RequiredOption,
): FieldResult {
  const raw = textOf(value);
  if (raw === null) return no('text.invalid_characters');
  const text = trimSpaces(raw);
  if (!text) return options.required === false ? ok('') : no('text.required');
  const count = codePointLength(text);
  if (count > options.max) return no('text.too_long', text, { max: options.max, count });
  const min = options.min ?? 0;
  if (count < min) return no('text.too_short', text, { min, count });
  const oneLine = options.oneLine ?? true;
  const controls = text.match(CONTROL_ALL) ?? [];
  if (controls.some((ch) => oneLine || !MULTILINE_ALLOWED.has(ch))) return no('text.invalid_characters', text);
  return ok(text);
}
